import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useLang } from "../contexts/LanguageContext";
import { useAuth } from "../contexts/AuthContext";
import { Menu, X, ArrowUpRight } from "lucide-react";

const LOGO = "https://lirp.cdn-website.com/514d3088/dms3rep/multi/opt/Logo+AP+VF-367w.png";

export default function Header() {
  const { lang, setLang, t } = useLang();
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  useEffect(() => {
    if (open) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  const nav = [
    { to: "/", label: t.nav.home },
    { to: "/coaching", label: t.nav.coaching },
    { to: "/outplacement", label: t.nav.outplacement },
    { to: "/ressources", label: t.nav.resources },
    { to: "/l-equipe", label: t.nav.coaches },
    { to: "/contact", label: t.nav.contact },
  ];

  return (
    <>
      <header
        data-testid="site-header"
        className={`fixed top-0 inset-x-0 z-40 transition-all duration-500 ${
          scrolled || open ? "bg-ivory/95 backdrop-blur-xl border-b border-black/5" : "bg-transparent"
        }`}
      >
        <div className="max-w-[1500px] mx-auto px-6 md:px-10 h-24 md:h-28 flex items-center justify-between gap-6">
          <Link to="/" data-testid="brand-link" className="flex items-center group">
            <img
              src={LOGO}
              alt="Audace & Perspectives"
              className={`w-auto transition-all duration-500 ${scrolled || open ? "h-14 md:h-16" : "h-16 md:h-20"}`}
            />
          </Link>

          <div className="flex items-center gap-3 md:gap-6">
            {/* Language switch — always visible outside menu */}
            <div data-testid="header-lang" className="flex items-center gap-1 font-mono text-[11px]">
              <button
                data-testid="header-lang-fr"
                onClick={() => setLang("fr")}
                className={`px-1.5 py-1 uppercase tracking-[0.18em] transition-colors ${lang === "fr" ? "text-brand" : "text-ink/45 hover:text-ink"}`}
              >FR</button>
              <span className="text-ink/20">/</span>
              <button
                data-testid="header-lang-en"
                onClick={() => setLang("en")}
                className={`px-1.5 py-1 uppercase tracking-[0.18em] transition-colors ${lang === "en" ? "text-brand" : "text-ink/45 hover:text-ink"}`}
              >EN</button>
            </div>

            {/* Espace confidentiel — always visible outside menu */}
            {user ? (
              <Link to="/dashboard" data-testid="header-dashboard" className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] font-mono text-brand hover:text-brandDark">
                <span className="w-1.5 h-1.5 bg-brand rounded-full"></span>
                <span className="hidden sm:inline">{t.nav.dashboard}</span>
              </Link>
            ) : (
              <Link to="/login" data-testid="header-login" className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] font-mono text-ink/70 hover:text-brand border border-ink/15 hover:border-brand px-2 sm:px-3 py-2">
                <span className="hidden sm:inline">{t.nav.login}</span>
                <span className="sm:hidden">●</span>
              </Link>
            )}

            <button
              data-testid="menu-trigger"
              aria-label={open ? t.nav.close : t.nav.menu}
              aria-expanded={open}
              onClick={() => setOpen((v) => !v)}
              className="group inline-flex items-center gap-3 md:gap-5 px-3 md:px-5 py-3 md:py-4 hover:bg-black/5 transition-colors"
            >
              <span className="flex flex-col gap-[5px] w-6">
                <span className={`h-px bg-ink transition-all duration-300 ${open ? "rotate-45 translate-y-[6px] w-full" : "w-full"}`}></span>
                <span className={`h-px bg-ink transition-all duration-300 ${open ? "opacity-0" : "w-4 ml-auto"}`}></span>
                <span className={`h-px bg-ink transition-all duration-300 ${open ? "-rotate-45 -translate-y-[6px] w-full" : "w-full"}`}></span>
              </span>
            </button>
          </div>
        </div>
      </header>

      {/* Overlay */}
      <div
        className={`fixed inset-0 z-40 bg-ink/20 backdrop-blur-md transition-opacity duration-500 ${open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"}`}
        onClick={() => setOpen(false)}
      ></div>

      {/* Drawer */}
      <aside
        data-testid="nav-drawer"
        aria-hidden={!open}
        className={`fixed top-0 right-0 z-50 h-full w-full sm:w-[360px] bg-ivory/30 backdrop-blur-2xl border-l border-white/20 shadow-[-30px_0_80px_-30px_rgba(15,23,42,0.18)] transform transition-transform duration-500 ease-[cubic-bezier(.2,.7,.2,1)] ${open ? "translate-x-0" : "translate-x-full"}`}
      >
        <div className="h-full flex flex-col">
          <div className="flex items-center justify-end px-6 md:px-8 pt-5 md:pt-6 pb-1">
            <button data-testid="menu-close" aria-label={t.nav.close} onClick={() => setOpen(false)} className="p-2 hover:bg-black/5 transition-colors">
              <X size={18} />
            </button>
          </div>

          <nav className="flex-1 overflow-y-auto no-scrollbar px-6 md:px-8 pt-2 pb-4">
            <ul className="space-y-0.5">
              {nav.map((n) => (
                <li key={n.to}>
                  <NavLink
                    to={n.to}
                    data-testid={`drawer-nav-${n.to.replace(/\//g, "") || "home"}`}
                    className={({ isActive }) =>
                      `group flex items-center justify-between gap-4 py-2 md:py-2.5 border-b border-ink/8 transition-all duration-300 ${
                        isActive ? "text-brand" : "text-navy hover:text-brand hover:pl-2"
                      }`
                    }
                  >
                    <span className="font-serif text-lg md:text-xl font-light tracking-tight leading-none truncate">{n.label}</span>
                    <ArrowUpRight size={14} className="text-current opacity-30 group-hover:opacity-100 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-all duration-300 flex-none" />
                  </NavLink>
                </li>
              ))}
            </ul>
          </nav>

          <div className="px-6 md:px-8 py-5 border-t border-ink/8">
            {user && (
              <button data-testid="drawer-logout" onClick={async () => { await logout(); navigate("/"); setOpen(false); }} className="text-xs text-ink/60 hover:text-ink uppercase tracking-[0.16em] font-mono">
                {t.nav.logout}
              </button>
            )}
            <div className="mt-2 text-[10px] text-ink/45 font-mono uppercase tracking-[0.2em]">Paris · Champs-Élysées</div>
          </div>
        </div>
      </aside>
    </>
  );
}
